import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { Kpi } from "@/components/admin/kpi";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { AdminIcon, type IconName } from "@/components/admin/icon";
import { useCustomersStats } from "@/lib/customers/customers-hooks";
import { customersApi } from "@/lib/customers/customers-api";
import { fmtFCFA } from "@/lib/format";

export function AdminCustomersStatsPage() {
	const { data, isLoading, isError } = useCustomersStats();
	const [hoveredBar, setHoveredBar] = useState<{ day: string; count: number } | null>(null);

	const { data: retentionData } = useQuery({
		queryKey: ["customers", "stats", "retention"],
		queryFn: () => customersApi.statsRetention(),
	});

	const { data: churnData } = useQuery({
		queryKey: ["customers", "stats", "churn"],
		queryFn: () => customersApi.statsChurn(),
	});

	if (isLoading) {
		return (
			<div style={{ padding: 40, textAlign: "center", color: "var(--a-ink-mute)" }}>
				<div style={{ display: "inline-block", animation: "pulse-akwaba 1.5s infinite" }}>
					Chargement des statistiques clients…
				</div>
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div style={{ padding: 40, textAlign: "center", color: "var(--a-danger)" }}>
				Erreur lors du chargement des statistiques. Veuillez réessayer.
			</div>
		);
	}

	const byDayList = data.acquisition.byDay ?? [];
	const maxByDay = Math.max(...byDayList.map((d) => d.count), 1);
	const total30d = byDayList.reduce((acc, curr) => acc + curr.count, 0);

	const bySourceList = data.acquisition.bySource ?? [];
	const totalSources = Math.max(bySourceList.reduce((acc, curr) => acc + curr.count, 0), 1);

	// Helper for source colors and icons
	const getSourceMeta = (sourceName: string): { icon: IconName; color: string; label: string } => {
		const s = sourceName.toLowerCase();
		if (s.includes("organic") || s.includes("organique") || s.includes("google") || s.includes("search")) {
			return { icon: "search", color: "var(--a-forest, #1e4d3b)", label: "Organique (Moteurs de recherche)" };
		}
		if (s.includes("referral") || s.includes("parrainage") || s.includes("lien")) {
			return { icon: "users", color: "var(--a-ochre, #d4a24c)", label: "Parrainage & Référence" };
		}
		if (s.includes("direct")) {
			return { icon: "arrowUR", color: "var(--a-terracotta, #c6563a)", label: "Accès direct" };
		}
		return { icon: "tag", color: "var(--a-info, #2b6a8e)", label: sourceName };
	};

	// Helper for cohort heatmap cell background
	const getHeatmapStyle = (pct: number | null | undefined) => {
		if (pct === null || pct === undefined) {
			return { bg: "var(--a-surface-2)", color: "var(--a-ink-mute)" };
		}
		if (pct === 100) return { bg: "#1e4d3b", color: "#ffffff", weight: 600 };
		if (pct >= 75) return { bg: "rgba(30, 77, 59, 0.75)", color: "#ffffff", weight: 600 };
		if (pct >= 50) return { bg: "rgba(30, 77, 59, 0.45)", color: "var(--a-ink)", weight: 500 };
		if (pct >= 25) return { bg: "rgba(30, 77, 59, 0.22)", color: "var(--a-ink)", weight: 500 };
		if (pct > 0) return { bg: "rgba(30, 77, 59, 0.1)", color: "var(--a-ink)", weight: 400 };
		return { bg: "var(--a-surface-2, #f2eee3)", color: "var(--a-ink-mute)", weight: 400 };
	};

	return (
		<>
			<PageHead
				title="Statistiques clients"
				sub="Acquisition · Activation · Rétention · Churn (CUS-08)"
			/>

			{/* Top KPI Cards */}
			<div className="kpi-grid" style={{ marginBottom: 24 }}>
				<Kpi
					label="Total clients"
					variant="accent"
					icon="users"
					value={data.acquisition.total.toLocaleString("fr-FR")}
					foot={<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>Base clients enregistrés</span>}
				/>
				<Kpi
					label="Activation 7j"
					variant="forest"
					icon="zap"
					value={
						<>
							{data.activation.rate7d}
							<small style={{ fontSize: "0.6em", marginLeft: 2 }}>%</small>
						</>
					}
					foot={<span style={{ fontSize: 11, color: "var(--a-forest)" }}>Première commande &lt; 7 jours</span>}
				/>
				<Kpi
					label="Activation 30j"
					variant="default"
					icon="percent"
					value={
						<>
							{data.activation.rate30d}
							<small style={{ fontSize: "0.6em", marginLeft: 2 }}>%</small>
						</>
					}
					foot={<span style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>Première commande &lt; 30 jours</span>}
				/>
				<Kpi
					label="Top 10% = CA"
					variant="warning"
					icon="star"
					value={
						<>
							{data.concentration.top10Pct}
							<small style={{ fontSize: "0.6em", marginLeft: 2 }}>%</small>
						</>
					}
					foot={<span style={{ fontSize: 11, color: "var(--a-warning)" }}>Part du CA générée par le Top 10%</span>}
				/>
			</div>

			{/* Visualisation Acquisition par jour (30 derniers jours) */}
			<Card style={{ marginBottom: 24 }}>
				<CardHead
					title="Acquisition quotidienne (30 derniers jours)"
					sub="Nombre de nouvelles inscriptions par jour"
					right={
						<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
							<Tag variant="neutral">
								Total 30j : <strong className="tn">{total30d}</strong>
							</Tag>
							<Tag variant="terra">
								Pic : <strong className="tn">{maxByDay}</strong>/j
							</Tag>
						</div>
					}
				/>
				<CardBody style={{ paddingTop: 12, paddingBottom: 20 }}>
					{byDayList.length === 0 ? (
						<div className="muted" style={{ padding: "30px 0", textAlign: "center" }}>
							Aucune inscription enregistrée sur les 30 derniers jours.
						</div>
					) : (
						<div style={{ position: "relative" }}>
							{/* Hover Tooltip display */}
							<div
								style={{
									height: 24,
									marginBottom: 8,
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-end",
									fontSize: 12,
								}}
							>
								{hoveredBar ? (
									<div
										style={{
											background: "var(--a-ink)",
											color: "#fff",
											padding: "3px 10px",
											borderRadius: "var(--a-r-sm, 6px)",
											boxShadow: "var(--a-shadow-sm)",
											display: "flex",
											alignItems: "center",
											gap: 8,
										}}
									>
										<span>
											{new Date(hoveredBar.day).toLocaleDateString("fr-FR", {
												day: "numeric",
												month: "short",
												year: "numeric",
											})}
										</span>
										<strong style={{ color: "var(--a-ochre, #d4a24c)" }}>
											{hoveredBar.count} inscription{hoveredBar.count > 1 ? "s" : ""}
										</strong>
									</div>
								) : (
									<span style={{ color: "var(--a-ink-mute)", fontSize: 11 }}>
										Survolez une barre pour voir les détails
									</span>
								)}
							</div>

							{/* Chart Box */}
							<div
								style={{
									display: "flex",
									alignItems: "flex-end",
									gap: 6,
									height: 140,
									paddingTop: 16,
									paddingBottom: 24,
									borderBottom: "1px dashed var(--a-border)",
									position: "relative",
								}}
							>
								{/* Grid reference lines */}
								<div
									style={{
										position: "absolute",
										top: 16,
										left: 0,
										right: 0,
										borderTop: "1px dashed var(--a-border)",
										opacity: 0.5,
										pointerEvents: "none",
									}}
								/>
								<div
									style={{
										position: "absolute",
										top: 70,
										left: 0,
										right: 0,
										borderTop: "1px dashed var(--a-border)",
										opacity: 0.3,
										pointerEvents: "none",
									}}
								/>

								{byDayList.map((d) => {
									const heightPct = Math.round((d.count / maxByDay) * 100);
									const isHovered = hoveredBar?.day === d.day;
									return (
										<div
											key={d.day}
											onMouseEnter={() => setHoveredBar(d)}
											onMouseLeave={() => setHoveredBar(null)}
											style={{
												flex: 1,
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 6,
												height: "100%",
												justifyContent: "flex-end",
												cursor: "pointer",
											}}
										>
											<div
												style={{
													width: "100%",
													maxWidth: 24,
													height: `${Math.max(heightPct, 6)}%`,
													background: d.count > 0
														? isHovered
															? "var(--a-terra-deep, #a04428)"
															: "linear-gradient(180deg, var(--a-terracotta, #c6563a) 0%, var(--a-terra-deep, #a04428) 100%)"
														: "var(--a-surface-2, #f2eee3)",
													borderRadius: "4px 4px 0 0",
													transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
													boxShadow: isHovered && d.count > 0 ? "0 4px 12px rgba(198, 86, 58, 0.35)" : "none",
													transform: isHovered ? "scaleY(1.04)" : "none",
													transformOrigin: "bottom",
												}}
											/>
											<span
												style={{
													fontSize: 10,
													color: isHovered ? "var(--a-ink)" : "var(--a-ink-mute)",
													fontWeight: isHovered ? 600 : 400,
													whiteSpace: "nowrap",
													position: "absolute",
													bottom: 0,
												}}
											>
												{d.day.slice(8)}
											</span>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</CardBody>
			</Card>

			{/* Middle Row: Rétention & Churn */}
			<div className="grid-2" style={{ marginBottom: 24 }}>
				{/* Cohortes de Rétention */}
				<Card>
					<CardHead
						title="Rétention par cohorte"
						sub="Taux de réachat J+30, J+60, J+90"
					/>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Cohorte</th>
									<th className="right">Effectif</th>
									<th className="center">J+30</th>
									<th className="center">J+60</th>
									<th className="center">J+90</th>
								</tr>
							</thead>
							<tbody>
								{(retentionData ?? []).length === 0 ? (
									<tr>
										<td colSpan={5} className="muted" style={{ textAlign: "center", padding: 20 }}>
											Aucune donnée de rétention disponible.
										</td>
									</tr>
								) : (
									(retentionData ?? []).map((r) => {
										const s30 = getHeatmapStyle(r.retention_30);
										const s60 = getHeatmapStyle(r.retention_60);
										const s90 = getHeatmapStyle(r.retention_90);
										return (
											<tr key={r.cohort}>
												<td>
													<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
														<AdminIcon n="calendar" s={14} c="var(--a-ink-mute)" />
														<strong>{r.cohort}</strong>
													</div>
												</td>
												<td className="right tn">
													<Tag variant="neutral">{r.size} client{r.size > 1 ? "s" : ""}</Tag>
												</td>
												<td className="center">
													<div
														style={{
															padding: "4px 8px",
															borderRadius: 6,
															background: s30.bg,
															color: s30.color,
															fontWeight: s30.weight,
															fontSize: 12,
															fontVariantNumeric: "tabular-nums",
														}}
													>
														{r.retention_30 ?? 0}%
													</div>
												</td>
												<td className="center">
													<div
														style={{
															padding: "4px 8px",
															borderRadius: 6,
															background: s60.bg,
															color: s60.color,
															fontWeight: s60.weight,
															fontSize: 12,
															fontVariantNumeric: "tabular-nums",
														}}
													>
														{r.retention_60 ?? 0}%
													</div>
												</td>
												<td className="center">
													<div
														style={{
															padding: "4px 8px",
															borderRadius: 6,
															background: s90.bg,
															color: s90.color,
															fontWeight: s90.weight,
															fontSize: 12,
															fontVariantNumeric: "tabular-nums",
														}}
													>
														{r.retention_90 ?? 0}%
													</div>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</Card>

				{/* Churn par Palier */}
				<Card>
					<CardHead
						title="Churn par palier"
						sub="Clients inactifs depuis > 90 jours sans commande"
					/>
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Palier</th>
									<th className="right">Total actifs</th>
									<th className="right">Inactifs &gt; 90j</th>
									<th className="right">Taux de churn</th>
								</tr>
							</thead>
							<tbody>
								{(churnData ?? []).length === 0 ? (
									<tr>
										<td colSpan={4} className="muted" style={{ textAlign: "center", padding: 20 }}>
											Aucune donnée de churn disponible.
										</td>
									</tr>
								) : (
									(churnData ?? []).map((c) => {
										const churnRate = c.total > 0 ? Math.round((c.churned / c.total) * 100) : 0;
										const tagVariant = churnRate > 50 ? "warning" : churnRate > 25 ? "warning" : "success";
										return (
											<tr key={c.tier}>
												<td>
													<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
														<AdminIcon n="tag" s={14} c="var(--a-ink-mute)" />
														<strong style={{ textTransform: "capitalize" }}>{c.tier}</strong>
													</div>
												</td>
												<td className="right tn">{c.total}</td>
												<td className="right tn">{c.churned}</td>
												<td className="right tn">
													<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
														{/* Mini progress bar */}
														<div
															style={{
																width: 48,
																height: 6,
																background: "var(--a-surface-2)",
																borderRadius: 3,
																overflow: "hidden",
															}}
														>
															<div
																style={{
																	width: `${churnRate}%`,
																	height: "100%",
																	background: churnRate > 50 ? "var(--a-danger)" : churnRate > 25 ? "var(--a-warning)" : "var(--a-success)",
																	borderRadius: 3,
																}}
															/>
														</div>
														<Tag variant={tagVariant} noDot>
															{churnRate}%
														</Tag>
													</div>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</Card>
			</div>

			{/* Bottom Row: Acquisition par source & Concentration CA */}
			<div className="grid-2">
				{/* Acquisition par source */}
				<Card>
					<CardHead
						title="Acquisition par source"
						sub="Répartition des inscriptions par canal d'origine"
					/>
					<CardBody>
						{bySourceList.length === 0 ? (
							<div className="muted" style={{ padding: "16px 0", textAlign: "center" }}>
								Aucune source enregistrée.
							</div>
						) : (
							<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
								{bySourceList.map((s) => {
									const meta = getSourceMeta(s.source);
									const pct = Math.round((s.count / totalSources) * 100);
									return (
										<div key={s.source}>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													marginBottom: 6,
												}}
											>
												<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
													<div
														style={{
															width: 28,
															height: 28,
															borderRadius: 6,
															background: "var(--a-surface-2)",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														<AdminIcon n={meta.icon} s={15} c={meta.color} />
													</div>
													<div>
														<div style={{ fontWeight: 600, textTransform: "capitalize" }}>
															{s.source}
														</div>
														<div style={{ fontSize: 11, color: "var(--a-ink-mute)" }}>
															{meta.label}
														</div>
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<strong className="tn" style={{ fontSize: 14 }}>
														{s.count.toLocaleString("fr-FR")}
													</strong>
													<span style={{ fontSize: 12, color: "var(--a-ink-mute)", marginLeft: 6 }}>
														({pct}%)
													</span>
												</div>
											</div>
											{/* Distribution bar */}
											<div
												style={{
													height: 7,
													width: "100%",
													background: "var(--a-surface-2)",
													borderRadius: 4,
													overflow: "hidden",
												}}
											>
												<div
													style={{
														height: "100%",
														width: `${pct}%`,
														background: meta.color,
														borderRadius: 4,
														transition: "width 0.3s ease",
													}}
												/>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</CardBody>
				</Card>

				{/* Concentration du CA */}
				<Card>
					<CardHead
						title="Concentration du CA"
						sub="Part du chiffre d'affaires par décile de clients"
					/>
					<CardBody>
						<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
							<ConcentrationRow
								label="Top 10% des clients"
								pct={data.concentration.top10Pct}
								color="var(--a-terracotta, #c6563a)"
							/>
							<ConcentrationRow
								label="Top 25% des clients"
								pct={data.concentration.top25Pct}
								color="var(--a-ochre, #d4a24c)"
							/>
							<ConcentrationRow
								label="Top 50% des clients"
								pct={data.concentration.top50Pct}
								color="var(--a-forest, #1e4d3b)"
							/>

							<div
								style={{
									marginTop: 10,
									padding: 14,
									borderRadius: "var(--a-r, 10px)",
									background: "linear-gradient(135deg, var(--a-surface-2, #f2eee3) 0%, var(--a-cream, #f7f1e8) 100%)",
									border: "1px solid var(--a-border)",
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
								}}
							>
								<div>
									<div style={{ fontSize: 12, color: "var(--a-ink-mute)", fontWeight: 500 }}>
										CA total cumulé (LTV)
									</div>
									<div style={{ fontSize: 11, color: "var(--a-ink-soft)" }}>
										Valeur de vie client globale
									</div>
								</div>
								<div style={{ fontSize: 16, fontWeight: 700, color: "var(--a-forest)" }} className="tn">
									{fmtFCFA(data.concentration.totalLtv)}
								</div>
							</div>
						</div>
					</CardBody>
				</Card>
			</div>
		</>
	);
}

function ConcentrationRow({
	label,
	pct,
	color,
}: {
	label: string;
	pct: number;
	color: string;
}) {
	return (
		<div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 6,
				}}
			>
				<span style={{ color: "var(--a-ink-soft)", fontWeight: 500 }}>{label}</span>
				<strong className="tn" style={{ color: "var(--a-ink)" }}>
					{pct}% <span style={{ fontSize: 11, fontWeight: 400, color: "var(--a-ink-mute)" }}>du CA</span>
				</strong>
			</div>
			<div
				style={{
					height: 6,
					width: "100%",
					background: "var(--a-surface-2)",
					borderRadius: 3,
					overflow: "hidden",
				}}
			>
				<div
					style={{
						height: "100%",
						width: `${Math.min(pct, 100)}%`,
						background: color,
						borderRadius: 3,
						transition: "width 0.3s ease",
					}}
				/>
			</div>
		</div>
	);
}

